/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.variantPicker = this.section.querySelector('variant-picker');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);
        window.addEventListener('resize', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
        this.syncInitialState();
        setTimeout(() => this.syncInitialState(), 800);
        setTimeout(() => this.normalizePreorderButton(), 150);
        setTimeout(() => this.normalizePreorderButton(), 600);
        setTimeout(() => this.normalizePreorderButton(), 1400);

        if (this.submitBtn && window.MutationObserver) {
          this.submitBtnObserver = new MutationObserver(() => this.normalizePreorderButton());
          this.submitBtnObserver.observe(this.submitBtn, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
        }

        if (this.classList.contains('sticky-atc--v1-figma')) {
          document.body.classList.add('v1-sticky-atc-enabled');
        }

        StickyAtc.handleScroll.call(this);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.throttledOnScroll);
        if (this.submitBtnObserver) this.submitBtnObserver.disconnect();
        if (this.classList.contains('sticky-atc--v1-figma')) {
          document.body.classList.remove('v1-sticky-atc-visible');
          document.body.classList.remove('v1-sticky-atc-enabled');
        }
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        const buyButtonsRect = this.buyButtons?.getBoundingClientRect();
        if (!buyButtonsRect) return;
        const showAfterMainAtc = this.dataset.showAfterMainAtc === 'true';

        let isOut;
        if (showAfterMainAtc) {
          // Only show after the main ATC has been scrolled past (ATC above viewport).
          isOut = !(buyButtonsRect.bottom <= topOffset);
        } else {
          // Keep sticky hidden while the on-page ATC is actually visible.
          // Show sticky when ATC is above or below viewport.
          const isMainAtcInView = buyButtonsRect.bottom > topOffset && buyButtonsRect.top < window.innerHeight;
          isOut = isMainAtcInView;
        }
        this.classList.toggle('sticky-atc--out', isOut);
        if (typeof this.normalizePreorderButton === 'function') {
          this.normalizePreorderButton();
        }

        if (this.classList.contains('sticky-atc--v1-figma')) {
          const stickyDisplay = window.getComputedStyle(this).display;
          const stickyIsRendered = stickyDisplay !== 'none';
          if (stickyIsRendered) {
            document.body.style.setProperty('--v1-sticky-atc-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
          }
          document.body.classList.toggle('v1-sticky-atc-visible', stickyIsRendered && !isOut);
        }

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Sync sticky state on initial load so text/price are correct before any variant event fires.
       */
      syncInitialState() {
        const variant = this.getCurrentVariant();
        const idInput = this.form?.querySelector('[name="id"]');

        if (variant) {
          if (this.variantTitle) this.variantTitle.textContent = variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = variant.id;
        } else if (this.variantContainer) {
          this.variantContainer.hidden = true;
        }

        this.updateAddToCartButton(variant);
      }

      /**
       * Get current variant from picker; fallback to sticky element dataset and embedded variant JSON.
       * @returns {object|null}
       */
      getCurrentVariant() {
        if (this.variantPicker && typeof this.variantPicker.getSelectedVariant === 'function') {
          const pickerVariant = this.variantPicker.getSelectedVariant();
          if (pickerVariant) return pickerVariant;
        }

        const fallbackId = Number(this.dataset.currentVariant || 0);
        if (!fallbackId) return null;

        if (!this.variantData) {
          const variantsDataEl = this.section.querySelector('variant-picker script[type="application/json"]');
          if (variantsDataEl) {
            try {
              const parsed = JSON.parse(variantsDataEl.textContent);
              if (parsed && Array.isArray(parsed.variants)) {
                this.variantData = parsed.variants;
              }
            } catch (e) {
              this.variantData = [];
            }
          }
        }

        if (Array.isArray(this.variantData)) {
          return this.variantData.find((variant) => Number(variant.id) === fallbackId) || null;
        }

        return null;
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = (variant
          ? theme.strings.noStock
          : this.submitBtn.dataset.unavailableText) || 'View details';
        const showPriceInButton = this.submitBtn.dataset.addToCartShowPrice === 'true';
        const defaultAddToCart = this.submitBtn.dataset.addToCartText || 'Add to cart';
        const isPreorderText = /pre[-\s]?order/i.test(defaultAddToCart);
        const isPreorderButton = this.submitBtn.classList.contains('gPreorderBtn') || isPreorderText;
        const shouldShowPrice = showPriceInButton && !isPreorderButton && typeof variant?.price === 'number';

        if (variantAvailable) {
          const addToCartText = defaultAddToCart;
          if (shouldShowPrice) {
            this.submitBtn.innerHTML = `<span class="sticky-atc__btn-label">${addToCartText}</span><span class="sticky-atc__btn-price"> - ${StickyAtc.formatMoney(variant.price)}</span>`;
          } else {
            this.submitBtn.textContent = addToCartText;
          }
        } else {
          this.submitBtn.textContent = unavailableStr;
        }

        this.normalizePreorderButton();
      }

      normalizePreorderButton() {
        if (!this.submitBtn) return;
        const rawText = (this.submitBtn.textContent || '').replace(/\s+/g, ' ').trim();
        const hasPreorderClass = this.submitBtn.classList.contains('gPreorderBtn');
        const hasPreorderText = /pre[-\s]?order/i.test(rawText);
        if (!hasPreorderClass && !hasPreorderText) return;

        const labelText = (this.submitBtn.querySelector('.sticky-atc__btn-label')?.textContent || '').trim();
        const normalizedText = labelText || 'Pre-Order';
        if (rawText !== normalizedText) {
          this.submitBtn.textContent = normalizedText;
        }
      }

      /**
       * Formats a price in cents to the shop currency format.
       * @param {number} cents - Price in cents.
       * @returns {string}
       */
      static formatMoney(cents) {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          const activeCurrency = window.Shopify.currency.active;
          const formatted = (cents / 100).toLocaleString(undefined, {
            style: 'currency',
            currency: activeCurrency
          });
          return StickyAtc.normalizeMoneyDisplay(formatted);
        }

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          const moneyFormat = (theme && theme.settings && theme.settings.moneyFormat)
            ? theme.settings.moneyFormat
            : theme.settings.moneyWithCurrencyFormat;
          const formatted = window.Shopify.formatMoney(cents, moneyFormat)
            .replace(/<[^>]*>/g, '')
            .trim();
          return StickyAtc.normalizeMoneyDisplay(formatted);
        }

        let activeCurrency = 'USD';
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          activeCurrency = window.Shopify.currency.active;
        }
        const formatted = (cents / 100).toLocaleString(undefined, { style: 'currency', currency: activeCurrency });
        return StickyAtc.normalizeMoneyDisplay(formatted);
      }

      /**
       * Remove currency code prefixes such as A$, US$, NZ$ and keep only symbol + amount.
       * @param {string} value
       * @returns {string}
       */
      static normalizeMoneyDisplay(value) {
        if (!value || typeof value !== 'string') return value;
        return value
          .trim()
          .replace(/^[A-Z]{1,4}\s?(?=[$£€¥])/u, '');
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
